import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Users, Clock, Star, ArrowRight, Filter, Loader } from 'lucide-react';

interface Event {
  id: number;
  title: string;
  date: string;
  location: string;
  category: string;
  image: string;
  description: string;
  highlights: string[];
  price: string;
  attendees: string;
  rating: number;
  maxAttendees?: number;
  currentAttendees?: number;
  status?: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
}

const categories = ["All", "Seasonal", "Cultural", "Business", "Luxury", "Sports"];

export default function EventsPage() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch events from API
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch('/api/events');
        
        if (!response.ok) {
          throw new Error('Failed to fetch events');
        }
        
        const data = await response.json();
        setEvents(data.events || []);
        setFilteredEvents(data.events || []);
      } catch (err) {
        console.error('Error fetching events:', err);
        setError('Unable to load events. Please try again later.');
        
        // Fallback to empty array
        setEvents([]);
        setFilteredEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    if (category === "All") {
      setFilteredEvents(events);
    } else {
      setFilteredEvents(events.filter((event: Event) => event.category === category));
    }
  };

  // Fallback image for events without images
  const getEventImage = (event: Event) => {
    if (event.image) {
      return event.image.startsWith('http') ? event.image : `https://raha-iduu.ainaeco.uk${event.image}`;
    }
    // Fallback to a default events image
    return "https://images.unsplash.com/photo-1549214499-34750c8cd8ec?w=800&h=600&fit=crop&auto=format&dpr=2";
  };

  const getStatusBadge = (status?: string) => {
    if (!status) return null;
    
    const statusConfig = {
      upcoming: { color: 'bg-green-500/20 border-green-500/50 text-green-200', label: 'Upcoming' },
      ongoing: { color: 'bg-blue-500/20 border-blue-500/50 text-blue-200', label: 'Ongoing' },
      completed: { color: 'bg-gray-500/20 border-gray-500/50 text-gray-200', label: 'Completed' },
      cancelled: { color: 'bg-red-500/20 border-red-500/50 text-red-200', label: 'Cancelled' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig];
    if (!config) return null;
    
    return (
      <span className={`px-2 py-1 text-xs rounded-full border ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen" style={ { backgroundColor: '#0b1a14' }}>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 z-0">
          {/* Background Image */}
          <img 
            src="https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80" 
            alt="Event Background" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b1a14] via-[#0b1a14]/90 to-[#0b1a14]/70"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#C4A77D] text-xs tracking-[0.3em] uppercase mb-4 block">Calendar of Excellence</span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl leading-tight mb-6 text-white" style={ { fontFamily: "'Playfair Display', serif" }}>
              Exclusive Events
            </h1>
            <p className="text-gray-300 text-lg md:text-xl font-light max-w-2xl mx-auto mb-10">
              Curated experiences for the discerning individual. Gain VIP access to the most sought-after gatherings.
            </p>

            <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <Calendar className="text-[#C4A77D]" size={18} />
                <span>Premium Access</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="text-[#C4A77D]" size={18} />
                <span>Exclusive Network</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="text-[#C4A77D]" size={18} />
                <span>Luxury Service</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 border-y border-white/5 bg-[#081210]">
        <div className="max-w-5xl mx-auto px-6 flex flex-wrap justify-center gap-3">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => handleCategoryChange(category)}
              className={`px-5 py-2 border text-xs uppercase tracking-wider transition-all duration-300 cursor-pointer ${
                selectedCategory === category
                  ? "bg-[#C4A77D] text-black border-[#C4A77D]"
                  : "border-[#C4A77D]/30 text-[#AABBAA] hover:border-[#C4A77D] hover:text-[#C4A77D]"
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </section>

      {/* Events Grid */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="text-center">
                <Loader className="animate-spin text-[#C4A77D] mx-auto mb-4" size={40} />
                <p className="text-gray-400">Loading events...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-gray-400 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 border border-[#C4A77D] text-[#C4A77D] text-xs uppercase tracking-wider transition-all duration-300 hover:bg-[#C4A77D] hover:text-black"
              >
                Try Again
              </button>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-400">No events found for this category.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredEvents.map((event: Event, index: number) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative overflow-hidden"
              >
                {/* Image */}
                <div className="aspect-[4/5] bg-gray-900 overflow-hidden">
                  <img
                    src={getEventImage(event)}
                    alt={event.title}
                    className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "https://images.unsplash.com/photo-1549214499-34750c8cd8ec?w=800&h=600&fit=crop&auto=format&dpr=2";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                </div>
                
                {/* Content Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-[#C4A77D] text-black text-[10px] font-bold uppercase tracking-wider px-2 py-1">
                      {event.category}
                    </span>
                    <span className="text-gray-400 text-xs">{event.date}</span>
                  </div>
                  <h3 className="text-xl text-white mb-2" style={ { fontFamily: "'Playfair Display', serif" }}>
                    {event.title}
                  </h3>
                  <p className="text-gray-400 text-sm font-light mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {event.description}
                  </p>
                  <a
                    href="#"
                    className="text-[#C4A77D] text-xs uppercase tracking-wider flex items-center gap-2 hover:gap-3 transition-all"
                  >
                    View Details 
                    <ArrowRight size={14} />
                  </a>
                </div>
              </motion.div>
            ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 border-t border-white/5 relative">
        <div className="absolute inset-0 opacity-5" style={ {backgroundImage: "url('https://www.transparenttextures.com/patterns/cubes.png')"}}></div>
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <span className="text-[#C4A77D] text-xs tracking-[0.3em] uppercase mb-4 block">Bespoke Service</span>
          <h2 className="text-3xl md:text-4xl text-white mb-6" style={ { fontFamily: "'Playfair Display', serif" }}>
            Ready for an Unforgettable Experience?
          </h2>
          <p className="text-gray-400 mb-10 max-w-xl mx-auto font-light">
            Let us create a bespoke event experience tailored to your preferences and requirements. From private dinners to corporate galas.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/curate-experience"
              className="px-8 py-3 border border-[#C4A77D] text-[#C4A77D] text-xs uppercase tracking-wider transition-all duration-300 bg-[#C4A77D] text-black hover:bg-white hover:border-white"
            >
              Curate Your Experience
            </a>
            <a
              href="/#contact"
              className="px-8 py-3 border border-[#C4A77D] text-[#C4A77D] text-xs uppercase tracking-wider transition-all duration-300 hover:bg-[#C4A77D] hover:text-black"
            >
              Contact Event Planner
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
